<template>
    <div v-if="steps.length" class="process">
        <button
            v-if="hasHiddenSteps"
            type="button"
            class="process-toggle"
            :aria-expanded="open"
            @click="open = !open"
        >
            <ChatIcon
                name="arrow-down"
                :size="12"
                class="process-caret"
                :class="{ 'process-caret--open': open }"
            />
            <span class="process-toggle-label">
                {{ open ? 'Скрыть процесс работы' : 'Показать процесс работы' }}
            </span>
            <span class="process-toggle-count">{{ stepCountLabel }}</span>
        </button>

        <div class="process-list">
            <div
                v-for="(step, idx) in visibleSteps"
                :key="step.id"
                class="process-step"
                :class="[
                    `process-step--${step.status}`,
                    {
                        'process-step--last': idx === visibleSteps.length - 1,
                        'process-step--terminal': step.terminal,
                    },
                ]"
            >
                <div class="process-rail" aria-hidden="true">
                    <span class="process-dot">
                        <ChatIcon v-if="step.terminal" name="check" :size="9" />
                    </span>
                    <span v-if="idx < visibleSteps.length - 1" class="process-line" />
                </div>

                <div class="process-body">
                    <div class="process-head">
                        <span class="process-tag">{{ step.tag }}</span>
                        <span class="process-title">{{ step.title }}</span>
                        <span
                            v-if="step.status === 'active'"
                            class="process-pulse"
                            aria-hidden="true"
                        />
                        <span v-if="step.durationLabel" class="process-duration">
                            {{ step.durationLabel }}
                        </span>
                    </div>
                    <p v-if="step.message && step.message !== step.title" class="process-message">
                        {{ step.message }}
                    </p>
                    <p v-if="step.detail" class="process-detail">{{ step.detail }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { resolveProgressMeta } from '@/components/chat/state/progressSteps'

const props = defineProps({
    steps: { type: Array, default: () => [] },
    streaming: { type: Boolean, default: false },
})

// По умолчанию таймлайн свёрнут: виден только последний (текущий/финальный)
// шаг. Остальные раскрываются по кнопке «Показать процесс работы».
const open = ref(false)

// «Часики» для живого подсчёта длительности активного шага. Тикают только пока
// есть активный шаг (во время стрима), чтобы не жечь CPU вхолостую.
const now = ref(Date.now())
let timer = null

function startTimer() {
    if (timer) return
    timer = setInterval(() => {
        now.value = Date.now()
    }, 500)
}

function stopTimer() {
    if (timer) {
        clearInterval(timer)
        timer = null
    }
}

const hasActiveStep = computed(() =>
    (props.steps || []).some(step => (step.status || 'done') === 'active'),
)

watch(
    hasActiveStep,
    (active) => {
        if (active) startTimer()
        else stopTimer()
    },
    { immediate: true },
)

onBeforeUnmount(stopTimer)

// tag/title могут отсутствовать у шагов, восстановленных из БД (PHP хранит
// минимальный набор stage/tool/message/detail/status/durationMs) — добираем из stage.
const displaySteps = computed(() =>
    (props.steps || []).map((step, idx) => {
        // tool-шаги маркируются по имени инструмента, прочие — по stage.
        const metaKey = step.stage === 'tool.start' && step.tool ? step.tool : (step.stage || '')
        const meta = resolveProgressMeta(metaKey)
        const status = step.status || 'done'
        return {
            id: step.id || `pstep-${idx}-${step.stage || 'step'}`,
            status,
            tag: step.tag || meta.tag,
            title: step.title || meta.title,
            message: step.message || '',
            detail: step.detail || '',
            durationLabel: stepDurationLabel(step, status),
        }
    }),
)

// Процесс завершён: не стримим и нет активных шагов. По завершении добавляем
// терминальный шаг «Готово», чтобы статус не выглядел так, будто ответ всё ещё
// формируется («финализирую ответ»).
const finished = computed(
    () => !props.streaming && !hasActiveStep.value && displaySteps.value.length > 0,
)

const allSteps = computed(() => {
    const base = displaySteps.value
    if (!finished.value) return base
    return [
        ...base,
        {
            id: 'pstep-done',
            status: 'done',
            terminal: true,
            tag: 'ГОТОВО',
            title: 'Ответ сформирован',
            message: '',
            detail: '',
            durationLabel: '',
        },
    ]
})

// Свёрнутый вид — только последний шаг; раскрытый — все.
const visibleSteps = computed(() =>
    open.value ? allSteps.value : allSteps.value.slice(-1),
)

const hasHiddenSteps = computed(() => allSteps.value.length > 1)

const stepCountLabel = computed(() => {
    const n = displaySteps.value.length
    return `${n} ${pluralize(n, ['шаг', 'шага', 'шагов'])}`
})

// Длительность шага: для завершённого — зафиксированная durationMs, для
// активного — живой счётчик от startedAt до текущего момента.
function stepDurationLabel(step, status) {
    if (status === 'active' && step.startedAt) {
        return formatDuration(now.value - step.startedAt)
    }
    const ms = step.durationMs
    if (ms === null || ms === undefined || ms < 0) return ''
    return formatDuration(ms)
}

function formatDuration(ms) {
    if (ms === null || ms === undefined || ms < 0) return ''
    if (ms < 1000) return `${Math.round(ms)} мс`
    const seconds = ms / 1000
    if (seconds < 60) {
        const value = seconds < 10 ? seconds.toFixed(1) : String(Math.round(seconds))
        return `${value.replace('.', ',')} с`
    }
    const min = Math.floor(seconds / 60)
    const rem = Math.round(seconds % 60)
    return rem ? `${min} мин ${rem} с` : `${min} мин`
}

function pluralize(n, forms) {
    const abs = Math.abs(n) % 100
    const last = abs % 10
    if (abs > 10 && abs < 20) return forms[2]
    if (last > 1 && last < 5) return forms[1]
    if (last === 1) return forms[0]
    return forms[2]
}
</script>

<style scoped>
.process {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.process-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    padding: 4px 8px;
    border: none;
    background: #f0f0f0;
    border-radius: 999px;
    font: inherit;
    font-size: 11px;
    color: #5f5f5f;
    cursor: pointer;
    transition: background-color 0.12s ease;
}

.process-toggle:hover {
    background: #e6e6e6;
}

.process-caret {
    color: #888888;
    transition: transform 0.15s ease;
}

.process-caret--open {
    transform: rotate(180deg);
}

.process-toggle-label {
    font-weight: 600;
    color: #23282c;
}

.process-toggle-count {
    color: #888888;
}

.process-list {
    display: flex;
    flex-direction: column;
}

.process-step {
    display: grid;
    grid-template-columns: 16px 1fr;
    gap: 0 10px;
    padding-bottom: 12px;
}

.process-step--last {
    padding-bottom: 0;
}

.process-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 4px;
}

.process-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #c0c0c0;
    flex-shrink: 0;
    z-index: 1;
    transition: background 0.2s ease, box-shadow 0.2s ease;
}

.process-step--active .process-dot {
    background: #23282c;
    box-shadow: 0 0 0 3px rgba(35, 40, 44, 0.12);
}

.process-step--done .process-dot {
    background: #23282c;
}

.process-step--terminal .process-dot {
    width: 16px;
    height: 16px;
    margin-top: -1px;
    background: #1f9d57;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.process-step--terminal .process-title {
    color: #1f9d57;
}

.process-step--terminal .process-tag {
    background: #e3f4ea;
    color: #1f9d57;
}

.process-line {
    flex: 1;
    width: 1px;
    min-height: 10px;
    margin-top: 4px;
    background: #e0e0e0;
}

.process-body {
    min-width: 0;
}

.process-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.process-tag {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #888888;
    padding: 3px 7px;
    background: #f0f0f0;
    border-radius: 999px;
}

.process-step--active .process-tag {
    background: #23282c;
    color: #fff;
}

.process-title {
    font-size: 12px;
    font-weight: 600;
    color: #23282c;
}

.process-duration {
    margin-left: auto;
    font-size: 11px;
    font-weight: 500;
    color: #9a9a9a;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.process-pulse {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #198fa0;
    animation: process-pulse 1.2s ease-in-out infinite;
}

@keyframes process-pulse {
    0%, 100% { opacity: 0.35; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.35); }
}

.process-message {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #5f5f5f;
}

.process-detail {
    margin: 3px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: #198fa0;
    font-weight: 500;
}
</style>
